/**
 * ProcessingLoader - Premium loading animation for credit report processing
 * 
 * Shows staged processing status with animated visuals while the deterministic
 * parser pipeline completes. Used during report upload and import.
 * 
 * PHASE 11.2: Do NOT render final report until processing is complete.
 * PHASE 11.4: Uses WebSocket for true real-time status updates from backend.
 * 
 * Props:
 * - isProcessing: Whether to show the loader
 * - currentStage: Manual stage message (overrides WebSocket)
 * - currentProgress: Manual progress percentage (0-100)
 * - importId: If provided, connects to WebSocket for real-time updates
 * - onComplete: Callback when import completes successfully
 * - onError: Callback when import fails
 * - onChallengeRequired: Callback when user input is needed (security question)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { cn } from '../../lib/utils';
import {
  buildImportCompleteResult,
  capImportProgress,
  classifyImportCompletion,
  fetchImportStatusOnce,
  getImportFailureMessage,
  getImportWebSocketUrl,
  IMPORT_ACCESS_DENIED_MESSAGE,
  IMPORT_COMPLETED_MISSING_REPORT_MESSAGE,
  IMPORT_OUTCOMES,
  IMPORT_SERVICE_UNAVAILABLE_MESSAGE,
  IMPORT_SESSION_EXPIRED_MESSAGE,
  IMPORT_STATUS_UPDATES_UNAVAILABLE_MESSAGE,
  isImportReadyForVerification,
  isImportSessionStalledProgress,
  isImportTerminalFailure,
  isStaleImportStatusResponse,
  MAX_AUTH_POLL_FAILURES,
  MAX_STATUS_POLL_FAILURES,
  normalizeImportStatusPayload,
  PROGRESS_CAP_INCOMPLETE,
  SESSION_STALL_MESSAGE,
  SESSION_STALL_TIMEOUT_MS,
  SLOW_IMPORT_MESSAGE,
  SLOW_IMPORT_POLL_INTERVAL_MS,
  SLOW_IMPORT_TIMEOUT_MS,
  verifyReportBelongsToImport,
} from '../../utils/creditImportStatus';

const PROCESSING_STAGES = [
  "Securing connection",
  "Verifying credentials",
  "Pulling report data",
  "Organizing tradelines",
  "Analyzing negative items",
  "Preparing dispute insights",
  "Opening your dashboard"
];

// Time between stage transitions (ms) for auto mode
const STAGE_INTERVAL = 2300;

// Polling interval for status API fallback (ms)
const POLL_INTERVAL = 2000;

export const ProcessingLoader = ({ 
  isProcessing = true, 
  currentStage = null,
  currentProgress = null,
  importId = null,
  importSession = null,
  getAuthHeaders = null,
  agencyId = null,
  onComplete = null,
  onError = null,
  onChallengeRequired = null,
  className 
}) => {
  const [statusIndex, setStatusIndex] = useState(0);
  const [status, setStatus] = useState({
    message: 'Initializing...',
    progress: 0,
    state: 'idle',
  });
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const pollAbortRef = useRef(null);
  const pollFailureCountRef = useRef(0);
  const authFailureCountRef = useRef(0);
  const stallStartedAtRef = useRef(null);
  const stallHandledRef = useRef(false);
  const terminalHandledRef = useRef(false);
  const activeImportIdRef = useRef(importId);
  const processingStartedAtRef = useRef(null);
  const [slowImportNotice, setSlowImportNotice] = useState(false);

  useEffect(() => {
    activeImportIdRef.current = importSession?.importId || importId || null;
  }, [importSession, importId]);

  useEffect(() => {
    if (!importId || !isProcessing) {
      processingStartedAtRef.current = null;
      setSlowImportNotice(false);
      return undefined;
    }
    processingStartedAtRef.current = Date.now();
    setSlowImportNotice(false);
    const timer = setInterval(() => {
      const startedAt = processingStartedAtRef.current;
      if (!startedAt) return;
      if (Date.now() - startedAt >= SLOW_IMPORT_TIMEOUT_MS) {
        setSlowImportNotice(true);
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [importId, isProcessing]);

  const stopTransport = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollAbortRef.current) {
      pollAbortRef.current.abort();
      pollAbortRef.current = null;
    }
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }
    wsRef.current = null;
    setWsConnected(false);
  }, []);

  const fireSessionStallError = useCallback(() => {
    if (stallHandledRef.current) return;
    stallHandledRef.current = true;
    stopTransport();
    onError?.({
      message: SESSION_STALL_MESSAGE,
      code: 'session_stalled',
    });
  }, [stopTransport, onError]);

  useEffect(() => {
    stallStartedAtRef.current = null;
    stallHandledRef.current = false;
    terminalHandledRef.current = false;
  }, [importId]);

  useEffect(() => {
    if (!importId || !isProcessing) {
      stallStartedAtRef.current = null;
      return undefined;
    }

    const checkStall = setInterval(() => {
      const startedAt = stallStartedAtRef.current;
      if (!startedAt || stallHandledRef.current) return;
      if (Date.now() - startedAt >= SESSION_STALL_TIMEOUT_MS) {
        fireSessionStallError();
      }
    }, 5000);

    return () => clearInterval(checkStall);
  }, [importId, isProcessing, fireSessionStallError]);
  
  // Auto-cycle through stages when not receiving real-time updates
  useEffect(() => {
    // If we have manual stage, importId, or WebSocket connected, don't auto-cycle
    if (!isProcessing || currentStage || importId) return;
    
    const interval = setInterval(() => {
      setStatusIndex(prev => (prev + 1) % PROCESSING_STAGES.length);
    }, STAGE_INTERVAL);
    
    return () => clearInterval(interval);
  }, [isProcessing, currentStage, importId]);

  const applyStatusDisplay = useCallback((normalized) => {
    const displayProgress = capImportProgress(
      normalized.progress ?? 0,
      normalized,
      PROGRESS_CAP_INCOMPLETE
    );

    setStatus({
      message: normalized.message || 'Processing...',
      progress: displayProgress,
      state: normalized.state || 'idle',
    });
  }, []);

  const processStatusUpdate = useCallback(async (data) => {
    const normalized = normalizeImportStatusPayload(data);
    const activeImportId = activeImportIdRef.current;
    if (isStaleImportStatusResponse(normalized, activeImportId)) {
      console.warn('[ProcessingLoader] Ignoring stale import status response', {
        activeImportId,
        responseImportId: normalized.import_id || normalized.import_job_id,
      });
      return 'stale';
    }

    const statusImportId = normalized.import_id || importSession?.importId || importId || 'unknown';
    const logPrefix = `[ProcessingLoader][${statusImportId}]`;
    console.log(`${logPrefix} Status update received`, {
      state: normalized.state,
      status: normalized.status,
      progress: normalized.progress,
      message: normalized.message,
      parser_success: normalized.parser_success,
      report_saved: normalized.report_saved,
      report_id: normalized.report_id,
      failure_reason: normalized.failure_reason,
      failure_message: normalized.failure_message,
    });
    applyStatusDisplay(normalized);

    if (isImportSessionStalledProgress(normalized)) {
      if (!stallStartedAtRef.current) {
        stallStartedAtRef.current = Date.now();
      }
    } else {
      stallStartedAtRef.current = null;
    }

    if (normalized.state === 'challenge_required') {
      onChallengeRequired?.({
        type: normalized.challenge_type,
        prompt: normalized.challenge_prompt,
      });
      return 'challenge_required';
    }

    if (isImportTerminalFailure(normalized)) {
      console.log(`${logPrefix} Decision: terminal failure -> show recovery`, {
        reason: normalized.failure_reason || normalized.state || normalized.status,
      });
      stopTransport();
      onError?.({
        message: getImportFailureMessage(normalized),
        code: normalized.failure_reason || normalized.state || normalized.status || 'unknown_import_failure',
        details: normalized,
      });
      return 'failed';
    }

    const statusCompleted =
      normalized.state === 'completed'
      || normalized.status === 'completed'
      || normalized.status === 'success';

    if (statusCompleted && !normalized.report_id) {
      console.error(`${logPrefix} Completed import missing report_id`, normalized);
      stopTransport();
      onError?.({
        message: IMPORT_COMPLETED_MISSING_REPORT_MESSAGE,
        code: 'missing_report_id',
        details: normalized,
      });
      return 'failed';
    }

    if (isImportReadyForVerification(normalized)) {
      const classification = classifyImportCompletion(normalized);
      if (classification.kind === 'failed') {
        stopTransport();
        onError?.({
          message: 'Import completed but no report was saved. Please retry or upload HTML manually.',
          code: classification.code || 'missing_report_id',
          details: normalized,
        });
        return 'failed';
      }

      const sessionImportId = importSession?.importId || importId;
      let verification = { verified: false, outcome: IMPORT_OUTCOMES.REVIEW_NEEDED };

      if (sessionImportId) {
        verification = await verifyReportBelongsToImport({
          reportId: normalized.report_id,
          importId: sessionImportId,
          importStartedAt: importSession?.startedAt,
          getAuthHeaders,
        });
      }

      const needsReview =
        classification.kind === 'review'
        || !verification.verified;

      const outcome = needsReview
        ? IMPORT_OUTCOMES.REVIEW_NEEDED
        : (verification.outcome || classification.outcome);

      if (terminalHandledRef.current) {
        stopTransport();
        return 'success';
      }
      terminalHandledRef.current = true;

      onComplete?.({
        ...buildImportCompleteResult(normalized, outcome),
        parser_success: normalized.parser_success !== false,
        report_saved: normalized.report_saved !== false,
        needs_review: needsReview,
      });
      stopTransport();
      return 'success';
    }

    return normalized.state || normalized.status;
  }, [
    applyStatusDisplay,
    importSession,
    importId,
    getAuthHeaders,
    onComplete,
    onError,
    onChallengeRequired,
    stopTransport,
  ]);

  // WebSocket connection
  useEffect(() => {
    if (!importId || !isProcessing) {
      stopTransport();
      return undefined;
    }
    
    const wsUrl = getImportWebSocketUrl(importId);
    
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('WebSocket connected for import:', importId);
        setWsConnected(true);
      };
      
      ws.onmessage = (event) => {
        (async () => {
          try {
            const data = JSON.parse(event.data);
            const terminal = await processStatusUpdate(data);

            if (terminal === 'success' || terminal === 'failed') {
              stopTransport();
            }
          } catch (e) {
            console.error('WebSocket message parse error:', e);
          }
        })();
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };
      
      ws.onclose = () => {
        console.log('WebSocket closed for import:', importId);
        setWsConnected(false);
      };
      
    } catch (error) {
      console.error('WebSocket connection failed:', error);
      setWsConnected(false);
    }
    
    return () => {
      stopTransport();
    };
  }, [importId, isProcessing, processStatusUpdate, stopTransport]);

  // Polling fallback when WebSocket is not connected.
  // Uses the shared authenticated API client (bearer + session cookie + agency scope).
  // A single failed poll never cancels the import; retries are bounded per error class.
  const pollStatus = useCallback(async () => {
    const activeId = importSession?.importId || importId;
    if (!activeId) return null;
    if (!pollAbortRef.current) {
      pollAbortRef.current = new AbortController();
    }

    let ok;
    let httpStatus;
    let body;
    try {
      ({ ok, status: httpStatus, body } = await fetchImportStatusOnce(activeId, {
        getAuthHeaders,
        agencyId,
        signal: pollAbortRef.current.signal,
      }));
    } catch (error) {
      if (error?.name === 'AbortError') return null;
      throw error;
    }

    if (ok) {
      pollFailureCountRef.current = 0;
      authFailureCountRef.current = 0;
      console.log('[ProcessingLoader][poll] Decision: status polled', {
        import_id: activeId,
        state: body?.state,
        status: body?.status,
      });
      return processStatusUpdate(body);
    }

    // Terminal: import does not exist on the backend.
    if (httpStatus === 404 && body?.state === 'not_found') {
      pollFailureCountRef.current = 0;
      return processStatusUpdate(body);
    }

    // Authorization errors: bounded retry, then a distinct auth message.
    // Never treated as a provider credential failure.
    if (httpStatus === 401 || httpStatus === 403) {
      authFailureCountRef.current += 1;
      console.error('Status poll error:', `status_http_${httpStatus}`);

      if (authFailureCountRef.current >= MAX_AUTH_POLL_FAILURES) {
        stopTransport();
        onError?.({
          message: httpStatus === 401
            ? IMPORT_SESSION_EXPIRED_MESSAGE
            : IMPORT_ACCESS_DENIED_MESSAGE,
          code: `status_http_${httpStatus}`,
        });
        return 'failed';
      }
      return null;
    }

    // Transient network/service errors: keep polling until the bounded limit.
    pollFailureCountRef.current += 1;
    const httpCode = httpStatus > 0 ? `status_http_${httpStatus}` : 'status_poll_failed';
    console.error('Status poll error:', httpCode);

    if (pollFailureCountRef.current >= MAX_STATUS_POLL_FAILURES) {
      stopTransport();
      onError?.({
        message: httpStatus > 0
          ? IMPORT_SERVICE_UNAVAILABLE_MESSAGE
          : IMPORT_STATUS_UPDATES_UNAVAILABLE_MESSAGE,
        code: httpCode,
      });
      return 'failed';
    }
    return null;
  }, [importSession, importId, getAuthHeaders, agencyId, processStatusUpdate, onError, stopTransport]);

  // Set up polling fallback when WebSocket is not available
  useEffect(() => {
    const activeId = importSession?.importId || importId;
    if (!activeId || !isProcessing) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }
    
    console.log('Starting polling for import:', activeId, 'WS Connected:', wsConnected);
    pollFailureCountRef.current = 0;
    pollStatus();
    
    const interval = wsConnected && !slowImportNotice
      ? POLL_INTERVAL
      : (slowImportNotice ? SLOW_IMPORT_POLL_INTERVAL_MS : 500);
    
    pollIntervalRef.current = setInterval(async () => {
      const terminal = await pollStatus();
      if (terminal === 'success' || terminal === 'failed') {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }, interval);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [importSession, importId, isProcessing, wsConnected, slowImportNotice, pollStatus]);

  // Determine what to display
  const displayStatus = currentStage || (importId ? status.message : PROCESSING_STAGES[statusIndex]);
  const displayProgress = currentProgress !== null ? currentProgress : (importId ? status.progress : null);

  return (
    <div className={cn("fe-loader-screen", className)}>
      <div className="fe-loader-shell">
        <div className="fe-loader-card">
          <div className="fe-loader-badge">Secure Import</div>

          <svg className="fe-loader-svg" viewBox="0 0 420 420" aria-label="Preparing your credit profile">
            <defs>
              <linearGradient id="feBrandRing" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#16B67A" />
                <stop offset="100%" stopColor="#0A5C46" />
              </linearGradient>

              <radialGradient id="feCoreGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#EAF8F2" />
                <stop offset="100%" stopColor="#F6F9F8" />
              </radialGradient>

              <filter id="feSoftGlow">
                <feGaussianBlur stdDeviation="8" result="blur" />
                <feMerge>
                  <feMergeNode in="blur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>

            {/* outer soft stage */}
            <circle cx="210" cy="210" r="150" fill="#F3F8F5"></circle>

            {/* orbit track */}
            <circle cx="210" cy="210" r="138" fill="none" stroke="#DCEBE4" strokeWidth="2" strokeDasharray="4 10"></circle>

            {/* static ring */}
            <circle cx="210" cy="210" r="102" fill="none" stroke="#D6E9E0" strokeWidth="10"></circle>

            {/* animated brand ring */}
            <circle className="fe-progress-ring" cx="210" cy="210" r="102" fill="none" stroke="url(#feBrandRing)" strokeWidth="10" strokeLinecap="round"></circle>

            {/* orbit: credit card */}
            <g className="orbit orbit-1">
              <g transform="translate(187 50)">
                <rect x="0" y="0" width="46" height="32" rx="8" fill="#ffffff" stroke="#0A5C46" strokeWidth="2.5"/>
                <rect x="0" y="8" width="46" height="7" fill="#E8F6F0"/>
                <rect x="8" y="21" width="12" height="4" rx="2" fill="#16B67A"/>
                <rect x="24" y="21" width="12" height="4" rx="2" fill="#BFD9CF"/>
              </g>
            </g>

            {/* orbit: house */}
            <g className="orbit orbit-2">
              <g transform="translate(315 215)">
                <path d="M10 20 L24 8 L38 20" fill="none" stroke="#0E7A5A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 18 V36 H34 V18" fill="#ffffff" stroke="#0E7A5A" strokeWidth="3" strokeLinejoin="round"/>
                <rect x="22" y="25" width="6" height="11" rx="2" fill="#DFF4EA"/>
              </g>
            </g>

            {/* orbit: car */}
            <g className="orbit orbit-3">
              <g transform="translate(44 222)">
                <path d="M8 26 L14 16 C16 12 18 10 23 10 H41 C46 10 49 13 51 17 L56 26" fill="#ffffff" stroke="#0A5C46" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                <rect x="12" y="18" width="40" height="11" rx="5.5" fill="#EAF8F2" stroke="#0A5C46" strokeWidth="3"/>
                <circle cx="19" cy="30" r="4.5" fill="#0A5C46"/>
                <circle cx="47" cy="30" r="4.5" fill="#0A5C46"/>
              </g>
            </g>

            {/* center core */}
            <g className="fe-center-core" filter="url(#feSoftGlow)">
              <circle cx="210" cy="210" r="56" fill="url(#feCoreGlow)"></circle>
              <circle className="fe-pulse-ring" cx="210" cy="210" r="66" fill="none" stroke="#BFE6D6" strokeWidth="3"></circle>

              {/* center document/shield icon */}
              <g transform="translate(182 178)">
                <path d="M28 0 L48 8 V26 C48 41 39 52 28 58 C17 52 8 41 8 26 V8 Z"
                      fill="#ffffff" stroke="#0A5C46" strokeWidth="3" strokeLinejoin="round"/>
                <path d="M20 20 H36" stroke="#16B67A" strokeWidth="3" strokeLinecap="round"/>
                <path d="M20 28 H34" stroke="#16B67A" strokeWidth="3" strokeLinecap="round"/>
                <path d="M20 36 H30" stroke="#16B67A" strokeWidth="3" strokeLinecap="round"/>
              </g>
            </g>
          </svg>

          <h2>Preparing Your Credit Profile</h2>
          <p className="fe-sub">
            We're securely importing your report and organizing your repair insights.
          </p>

          <div className="fe-status-card">
            <div className="fe-status-head">
              <span className="fe-status-live"></span>
              <span>Import in progress</span>
            </div>
            <div className="fe-status-text" id="fe-status-text" data-testid="processing-status">
              {slowImportNotice && isProcessing
                ? SLOW_IMPORT_MESSAGE
                : displayStatus}
            </div>
            <div className="fe-progress-bar">
              <div 
                className="fe-progress-bar-fill" 
                style={displayProgress !== null ? { 
                  width: `${displayProgress}%`, 
                  animation: 'none' 
                } : undefined}
              ></div>
            </div>
            {displayProgress !== null && (
              <div className="fe-progress-text" style={{
                fontSize: '12px',
                color: 'var(--fe-muted)',
                marginTop: '8px',
                textAlign: 'right'
              }}>
                {displayProgress}%
              </div>
            )}
          </div>

          <div className="fe-trust-row">
            <span>Encrypted session</span>
            <span>No score impact</span>
            <span>Secure retrieval</span>
          </div>
        </div>
      </div>

      <style>{`
        :root{
          --fe-bg: #F6F9F8;
          --fe-card: #FFFFFF;
          --fe-text: #1D2B2A;
          --fe-muted: #6C7A79;
          --fe-line: #DCEBE4;
          --fe-brand-dark: #0A5C46;
          --fe-brand: #0E7A5A;
          --fe-accent: #16B67A;
          --fe-accent-soft: #EAF8F2;
          --fe-shadow: 0 18px 50px rgba(7, 31, 24, 0.08);
        }

        .fe-loader-screen{
          min-height: 60vh;
          display:flex;
          align-items:center;
          justify-content:center;
          background: linear-gradient(180deg, #F8FBFA 0%, #F2F7F5 100%);
          padding: 28px;
          font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          border-radius: 24px;
        }

        .fe-loader-shell{
          width:100%;
          max-width:760px;
        }

        .fe-loader-card{
          background: var(--fe-card);
          border: 1px solid rgba(10, 92, 70, 0.08);
          box-shadow: var(--fe-shadow);
          border-radius: 28px;
          padding: 26px 26px 24px;
          text-align:center;
        }

        .fe-loader-badge{
          display:inline-flex;
          align-items:center;
          gap:8px;
          padding:8px 14px;
          border-radius:999px;
          background: var(--fe-accent-soft);
          color: var(--fe-brand-dark);
          font-size:13px;
          font-weight:700;
          letter-spacing:.01em;
          margin-bottom: 14px;
        }

        .fe-loader-svg{
          width:100%;
          max-width:360px;
          display:block;
          margin:0 auto 12px;
        }

        .fe-loader-card h2{
          margin: 0 0 8px;
          font-size: 34px;
          line-height: 1.08;
          color: var(--fe-text);
          font-weight: 800;
          letter-spacing: -0.03em;
        }

        .fe-sub{
          margin: 0 auto 18px;
          max-width: 520px;
          font-size: 16px;
          line-height: 1.6;
          color: var(--fe-muted);
        }

        .fe-status-card{
          max-width: 470px;
          margin: 0 auto;
          background: #F9FCFB;
          border: 1px solid var(--fe-line);
          border-radius: 18px;
          padding: 14px 16px 16px;
          text-align:left;
        }

        .fe-status-head{
          display:flex;
          align-items:center;
          gap:10px;
          color: var(--fe-brand-dark);
          font-size: 13px;
          font-weight: 700;
          margin-bottom: 8px;
        }

        .fe-status-live{
          width:10px;
          height:10px;
          border-radius:999px;
          background: var(--fe-accent);
          box-shadow: 0 0 0 6px rgba(22,182,122,.12);
          animation: feDot 1.8s ease-in-out infinite;
        }

        .fe-status-text{
          font-size: 17px;
          font-weight: 700;
          color: var(--fe-text);
          margin-bottom: 12px;
        }

        .fe-progress-bar{
          height: 8px;
          background: #E8F3EE;
          border-radius: 999px;
          overflow: hidden;
        }

        .fe-progress-bar-fill{
          width: 42%;
          height: 100%;
          border-radius: inherit;
          background: linear-gradient(90deg, var(--fe-accent), var(--fe-brand-dark));
          animation: feBar 2.6s ease-in-out infinite;
        }

        .fe-trust-row{
          display:flex;
          justify-content:center;
          gap:20px;
          flex-wrap:wrap;
          margin-top:14px;
          color: var(--fe-muted);
          font-size: 13px;
          font-weight: 600;
        }

        .orbit{ transform-origin: 210px 210px; }
        .orbit-1{ animation: feOrbit 12s linear infinite; }
        .orbit-2{ animation: feOrbitReverse 16s linear infinite; }
        .orbit-3{ animation: feOrbit 18s linear infinite; }

        .fe-progress-ring{
          stroke-dasharray: 640;
          stroke-dashoffset: 280;
          transform-origin: 50% 50%;
          animation: feRingDash 3.2s ease-in-out infinite;
        }

        .fe-center-core{
          animation: feFloat 4.4s ease-in-out infinite;
        }

        .fe-pulse-ring{
          animation: fePulse 2.8s ease-in-out infinite;
        }

        @keyframes feOrbit{
          from{ transform: rotate(0deg); }
          to{ transform: rotate(360deg); }
        }

        @keyframes feOrbitReverse{
          from{ transform: rotate(360deg); }
          to{ transform: rotate(0deg); }
        }

        @keyframes feRingDash{
          0%{ stroke-dashoffset: 640; opacity:.85; }
          50%{ stroke-dashoffset: 180; opacity:1; }
          100%{ stroke-dashoffset: 640; opacity:.85; }
        }

        @keyframes feFloat{
          0%,100%{ transform: translateY(0); }
          50%{ transform: translateY(-4px); }
        }

        @keyframes fePulse{
          0%,100%{ opacity:.38; transform: scale(.98); transform-origin:210px 210px; }
          50%{ opacity:.95; transform: scale(1.02); transform-origin:210px 210px; }
        }

        @keyframes feDot{
          0%,100%{ transform: scale(1); opacity:1; }
          50%{ transform: scale(1.2); opacity:.7; }
        }

        @keyframes feBar{
          0%{ width: 28%; }
          50%{ width: 82%; }
          100%{ width: 36%; }
        }

        @media (max-width: 640px){
          .fe-loader-card{
            padding: 22px 18px 20px;
            border-radius: 24px;
          }

          .fe-loader-card h2{
            font-size: 28px;
          }

          .fe-sub{
            font-size: 15px;
          }

          .fe-status-text{
            font-size: 16px;
          }

          .fe-trust-row{
            gap: 12px;
            font-size: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default ProcessingLoader;
